import { needsCsrf, readCsrfCookie, CSRF_HEADER } from './csrf';
import { ApiError, refreshCsrf } from './client';

export type CopilotSseHandlers = {
  onMeta?: (data: {
    session_id: string;
    source_ids: string[];
    read_tools?: boolean;
  }) => void;
  onTool?: (data: { name: string; status: 'start' | 'done'; error?: string }) => void;
  onToken?: (text: string) => void;
  onDone?: (data: {
    session_id: string;
    source_ids: string[];
    content?: string;
    model?: string;
    latency_ms?: number;
  }) => void;
  onError?: (data: { message: string; code?: string }) => void;
  onStatus?: (data: { phase: string; reason?: string }) => void;
  onTimeline?: (data: { step: string }) => void;
};

function parseSseBlock(block: string, handlers: CopilotSseHandlers): void {
  let event = 'message';
  let dataLine = '';
  for (const line of block.split('\n')) {
    if (line.startsWith('event:')) event = line.slice(6).trim();
    if (line.startsWith('data:')) dataLine = line.slice(5).trim();
  }
  if (!dataLine) return;
  try {
    const data = JSON.parse(dataLine) as Record<string, unknown>;
    if (event === 'meta') handlers.onMeta?.(data as Parameters<NonNullable<CopilotSseHandlers['onMeta']>>[0]);
    if (event === 'tool') handlers.onTool?.(data as Parameters<NonNullable<CopilotSseHandlers['onTool']>>[0]);
    if (event === 'token') handlers.onToken?.((data as { text: string }).text);
    if (event === 'done') handlers.onDone?.(data as Parameters<NonNullable<CopilotSseHandlers['onDone']>>[0]);
    if (event === 'error') handlers.onError?.(data as { message: string; code?: string });
    if (event === 'status') handlers.onStatus?.(data as { phase: string; reason?: string });
    if (event === 'timeline') handlers.onTimeline?.(data as { step: string });
  } catch {
    /* ignore */
  }
}

export async function copilotChatStream(
  body: { project_id: string; session_id?: string; message: string },
  handlers: CopilotSseHandlers,
): Promise<void> {
  const headers = new Headers({ 'Content-Type': 'application/json' });
  if (needsCsrf('POST')) {
    let token = readCsrfCookie();
    if (!token) token = await refreshCsrf();
    if (token) headers.set(CSRF_HEADER, token);
  }

  const res = await fetch('/api/copilot/chat', {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const err = (await res.json().catch(() => null)) as { error?: string; code?: string } | null;
    throw new ApiError(res.status, err?.error ?? res.statusText, err?.code);
  }

  if (!res.body) throw new ApiError(502, 'Geen stream', 'NO_BODY');

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop() ?? '';
    for (const block of parts) {
      if (block.trim()) parseSseBlock(block, handlers);
    }
  }
  if (buffer.trim()) parseSseBlock(buffer, handlers);
}
